// src/config/plotConfigs/subcodonProfileConfig.js
export const subcodonProfileConfig = {
  sections: [
    {
      fields: [
        {
          id: "transcript",
          label: "Gene/Transcript",
          type: "text",
          placeholder: "ENST00000368885",
          tooltip:
            "The gene or transcript to be plotted. If a gene is entered you will be presented with a list of all transcripts associated with that gene (unless only a single transcript exists). One of the transcripts shown will be designated the 'principal' isoform, this will either be based on the transcript with the longest CDS or based on APPRIS annotations, the method used to determine the principal isoform is shown in brackets in the header of the 'Type' column.",
          width: 6,
          mode: "single", // Only show in single mode
        },
        {
          id: "minTripletScore",
          label: "Min triplet periodicity score",
          type: "number",
          placeholder: "0",
          min: 0,
          max: 1,
          step: 0.1,
          tooltip:
            "This is a simple metric to include/exclude readlengths from a particular file depending on how good the triplet periodicity is. For each readlength a score is calculated from it's triplet periodicity plot using the formula 1-(count of the second highest peak/count of the highest peak), thus read lengths with strong triplet periodicity will be close to 1, while readlengths with poor triplet periodicity will be close to 0. Any readlengths from any files with a read score below what is entered in this field will not be displayed, thus entering a value close to 1 will result in better triplet periodicity but less data.",
          width: 6, // ✅ Changed to full width since it will be alone in batch mode
          advanced: true,
        },
        {
          id: "highlightSequences",
          label: "Highlight sequences list",
          type: "text",
          placeholder: "Enter sequences",
          tooltip:
            "This text box allows you to input a comma seperated list of nucleotide sequences which will be highligted in the open reading frame architecture of the resulting plot. By default these will appear as short black lines but their colour can be changed in the settings. These can be turned on/off by clicking the 'Highlighted sequences' button to the right of the plot. Note: the following IUPAC codes are also recognised: R: A/G, Y: C/U, S: G/C, W: A/U, K: G/U, M: A/C, B: C/G/U, D: A/G/U, H: A/C/U, V: A/C/G, N: A/C/G/U",
          width: 12,
          advanced: true,
        },

        {
          id: "minRead",
          label: "Min Read",
          type: "number",
          placeholder: "20",
          min: 1,
          max: 1000,
          tooltip:
            "The minimum read length to be displayed on the graph. Any reads below this length will be ignored.",
          width: 3, // Keep as 6 - half width
        },
        {
          id: "maxRead",
          label: "Max Read",
          type: "number",
          placeholder: "150",
          min: 1,
          max: 1000,
          tooltip:
            "The maximum read length to be displayed on the graph. Any reads above this length will be ignored.",
          width: 3, // Keep as 6 - half width
        },

        {
          id: "highlightStart",
          label: "Highlight start",
          type: "text",
          placeholder: "Enter comma-separated numbers (e.g., 10,80)",
          tooltip:
            "Highlight start/Highlight stop can be used to highlight a specific region of a transcript. Filling in a value for highlight start and highlight stop will colour in that region of the plot. Multiple regions can be highlighted by entering a comma seperated list of numbers, for example to highlight the regions between 10-20 nts and 80-90 nts, enter 10,80 in the Highlight start input box and 20,90 in the Highlight stop input box.",
          width: 6,
          mode: "single",
          advanced: true,
        },
        {
          id: "highlightStop",
          label: "Highlight stop",
          type: "text",
          placeholder: "Enter comma-separated numbers (e.g., 20,90)",
          tooltip:
            "Highlight start/Highlight stop can be used to highlight a specific region of a transcript. Filling in a value for highlight start and highlight stop will colour in that region of the plot. Multiple regions can be highlighted by entering a comma seperated list of numbers, for example to highlight the regions between 10-20 nts and 80-90 nts, enter 10,80 in the Highlight start input box and 20,90 in the Highlight stop input box.",
          width: 6,
          mode: "single",
          advanced: true,
        },
        {
          id: "offsetDirection",
          label: "Offset Direction",
          type: "radio",
          options: [
            { value: "fiveprime", label: "5'" },
            { value: "threeprime", label: "3'" },
          ],
          tooltip:
            "Infer the a-site of each ribosome protected fragment from either the 5' or 3' end of each read.",
          width: 12,
          advanced: true,
        },
        {
          id: "riboSeqCoverage",
          label: "Ribo-Seq coverage",
          type: "checkbox",
          tooltip:
            "If checked, every position of every read contributes to the data displayed on the plot, if unchecked only the inferred a-site of the read contributes to the plot.",
          width: 12,
          advanced: true,
        },
        {
          id: "ambig_check",
          label: "Allow ambiguous reads",
          type: "checkbox",
          tooltip:
            "Checking this box means that both ambiguously mapped reads and unambiguously mapped reads will be displayed. A read is considered ambiguous if it maps to 2 or 3 different genes. Ambiguous reads are weighted according to the number of genes they align to, reads that align to two genes have a count of 0.5 while reads that align to 3 genes have a count of 0.33 .",
          width: 3,
          mode: "batch", // Only show in batch mode
          advanced: true,
        },
        {
          id: "email",
          label: "Email notification",
          type: "checkbox",
          tooltip: "Send email when analysis is complete",
          mode: "batch", // Only show in batch mode
          width: 12,
        },
      ],
    },
  ],
};

export const subcodonProfileDefaults = {
  transcript: "ENST00000316292",
  minTripletScore: 0,
  minRead: 20,
  maxRead: 150,
  highlightSequences: "",
  highlightStart: "0",
  highlightStop: "0",
  offsetDirection: "fiveprime",
  riboSeqCoverage: false,
  email: true,
  plot_url: "/interactive_plot/",
};

export const subcodonProfileValidation = {
  transcript: { required: true, label: "Gene/Transcript" },
  minRead: { required: true, min: 1, max: 1000, label: "Min Read" },
  maxRead: { required: true, min: 1, max: 1000, label: "Max Read" },
  minTripletScore: { min: 0, max: 1, label: "Min triplet periodicity score" },
};

// Add batch-specific defaults and validation
export const batchSubcodonProfileDefaults = {
  ...subcodonProfileDefaults,
  // Batch-specific defaults will be handled by useBatchSettings hook
};

export const batchSubcodonProfileValidation = {
  ...subcodonProfileValidation,
  // Additional batch validations can be added here if needed
};
